import React, { useEffect, useMemo, useState, useRef } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { ManualPaymentModalLayoutJSON } from "../Json/ManualPaymentLayoutJSON";
import { ManualPaymentModalFieldJSON } from "../Json/ManualPaymentFieldJSON";
import { renderElement } from "../../../../Components/Common/FormRendererV2/Renderer";
import { useDispatch, useSelector } from "react-redux";
import {
  saveManualPaymentMode,
  updateManualPaymentMode,
} from "../OrderService";
import {
  setEditManualPaymentModalDropdown,
  setHandleManualPaymentModal,
  setManualPaymentJson,
} from "../../../../slices/orders/orderSlice";
import { getCategoryDropdown } from "../../../../Utils/CommonService";
import rootReducer from "../../../../store";
import HubDocuments from "../../../../Components/Common/Documents/HubDocuments";
import DocumentUploader from "../../../../Components/Common/Documents/Documents";
import {
  resetFormState,
  setRendererFormState,
  setSelectValidError,
  updateRendererFormState,
} from "../../../../slices/common/reducer";

function ManualPaymentModal({
  modalShow,
  handleClose,
  handleEditModalOpen,
  handleEditModalClose,
}) {
  const formState = useSelector((state) => state.common.formRendererState);
  const editManualPaymentModal = useSelector(
    (state) => state.order.selectedOrders.handleEditManualPaymentModal
  );
  const dispatch = useDispatch();
  const manualData = useSelector(
    (state) => state.order.selectedOrders.manualPaymentDetails
  );
  const manualPaymentFieldJson = useSelector(
    (state) => state.order.selectedOrders.manualPaymentFieldJson
  );

  const orderDetails = useSelector(
    (state) => state.order.selectedOrders.detailsData
  );

  const [isEditing, setIsEditing] = useState({});
  const [filesFetched, setFilesFetched] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const files = useSelector(
    (state) => state.order.selectedOrders.manualPaymentDetails.file || {}
  );

  const fieldsJson = useMemo(() => {
    if (!manualPaymentFieldJson) return manualPaymentFieldJson;
    if (isSubmitting) {
      const updatedFields = {};
      Object.keys(manualPaymentFieldJson).forEach((key) => {
        updatedFields[key] = {
          ...manualPaymentFieldJson[key],
          "display-disabled": 1,
        };
      });
      return updatedFields;
    }
    return manualPaymentFieldJson;
  }, [manualPaymentFieldJson, isSubmitting]);

  useEffect(() => {
    if (modalShow) {
      dispatch(updateRendererFormState({}));
      isSubmittingRef.current = false;
    }

    return () => {
      dispatch(updateRendererFormState(formState));
    };
  }, [modalShow, dispatch]);

  useEffect(() => {
    const hasFilledField = Object.values(formState).some(
      (value) =>
        value !== undefined && value !== null && value.toString().trim() !== ""
    );

    setIsFormValid(hasFilledField);
  }, [formState]);

  useEffect(() => {
    const getManualPaymentDropdown = async () => {
      const res = await getCategoryDropdown(
        "PMT",
        "mode",
        ManualPaymentModalFieldJSON
      );
    };

    getManualPaymentDropdown();
  }, []);

  const handleSavePayment = (isDraft) => {
    const file = formState?.payment_slip_media_id
      ? formState.payment_slip_media_id
      : manualData.file?.payment_slip_media_id?.media_url;

    const allowedFields = [
      "amount",
      "mode",
      "payment_date",
      "txn_id",
      "note",
      "payment_slip_media_id",
      "order_id",
    ];
    const filteredFormState = allowedFields.reduce((acc, key) => {
      if (formState[key] !== undefined) {
        acc[key] = formState[key];
      }
      return acc;
    }, {});

    const updatedFormState = {
      ...filteredFormState,
      is_draft: isDraft ? 1 : 0,
      parentid: editManualPaymentModal
        ? manualData.parentid
        : orderDetails.entityid,
      parenttype: "ODR",
      entityid: editManualPaymentModal ? manualData.entityid : undefined,
    };

    if (!editManualPaymentModal) {
      delete updatedFormState.entityid;
    }
    dispatch(updateRendererFormState(updatedFormState));
    if (!updatedFormState.mode) {
      dispatch(setSelectValidError(true));
    } else {
      handleSaveManualPayment(updatedFormState, file);
    }
  };

  const toggleEdit = (fieldKey) => {
    setIsEditing((prev) => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting || isSubmittingRef.current) return;
    handleSavePayment(false);
  };

  const handleSaveDraft = (e) => {
    e.preventDefault();
    if (isSubmitting || isSubmittingRef.current) return;
    if (isFormValid) {
      handleSavePayment(true);
    }
  };

  useEffect(() => {
    if (!modalShow) {
      dispatch(setEditManualPaymentModalDropdown(false));
    }
  }, [modalShow, dispatch]);

  const renderField = (child) => {
    if (child.elementtype === "column") {
      return (
        <div className={child.colsizeclass} key={child.elementid}>
          {child?.child?.map((nestedChild) => renderField(nestedChild))}
        </div>
      );
    }

    if (child.elementtype === "documentFile") {
      return (
        <HubDocuments
          key={child.elementid}
          documentLayout={[child]}
          documentFields={fieldsJson}
          formState={formState}
          files={files}
          isEditing={isEditing}
          toggleEdit={toggleEdit}
          showUploadButtonByDefault={filesFetched}
          fetched={filesFetched}
          filename={true}
          isDisabled={isSubmitting}
        />
      );
    }

    return renderElement(child, fieldsJson, formState);
  };

  const renderManualPaymentJson = () => {
    if (!manualPaymentFieldJson) return <p>No profile data available</p>;

    const formatDate = (value) => {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return value;
    };

    const renderChildFields = (childData) => {
      const fields = [];

      childData?.forEach((child, index) => {
        if (
          child.elementtype === "file" ||
          child.elementtype === "textfield" ||
          child.elementtype === "email" ||
          child.elementtype === "customDate" ||
          child.elementtype === "number" ||
          child.elementtype === "textarea" ||
          child.elementtype === "customSelect"
        ) {
          const labelKey = `${child.elementid}-${child.elementtype}`;
          const fieldLabel =
            manualPaymentFieldJson[labelKey]?.["display-fieldlabel"] ||
            child.elementid;
          const databaseLabel =
            manualPaymentFieldJson[labelKey]?.["database-label"];
          let fieldValue = formState[databaseLabel];

          if (child.elementtype === "file") {
            const fileData = formState.files?.[databaseLabel];
            fieldValue = fileData?.file_name || "-";
          } else {
            fieldValue = formState[databaseLabel];
          }

          const formattedValue =
            fieldValue &&
            typeof fieldValue === "string" &&
            fieldValue.match(/^\d{4}-\d{2}-\d{2}/)
              ? formatDate(fieldValue)
              : fieldValue;

          fields.push(
            <Col span={12} className="form-view-field" key={child.elementid}>
              <div className="view-label">{fieldLabel} :</div>
              <div className="view-value">{formattedValue || "-"}</div>
            </Col>
          );
        }

        if (child.child) {
          fields.push(renderChildFields(child.child));
        }
      });

      return fields;
    };

    return (
      <div>
        <div className="proposal-container">
          <div className="form-json-view">
            {ManualPaymentModalLayoutJSON.map((element, ind) => {
              return (
                <Row className="form-row">
                  {element.child && renderChildFields(element.child)}
                </Row>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const handleSaveManualPayment = async (entity, file) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const res = await saveManualPaymentMode(entity, manualData.entityid, file);
      if (res && formState) {
        rootReducer.dispatch(setHandleManualPaymentModal(false));
      }
    } catch (error) {
      console.error(error);
      isSubmittingRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (!isSubmitting) {
      handleClose();
    }
  };

  return (
    <Modal
      isOpen={modalShow}
      onHide={handleModalClose}
      centered
      size="lg"
      backdrop={isSubmitting ? "static" : true}
      keyboard={!isSubmitting}
    >
      <Form className="tablelist-form" onSubmit={handleSubmit}>
        <ModalHeader
          className="p-3 bg-info-subtle"
          toggle={isSubmitting ? undefined : handleModalClose}
        >
          {manualData?.payment_status === "Success"
            ? "Payment"
            : editManualPaymentModal
            ? "Update Payment"
            : "Add Payment"}
        </ModalHeader>
        <ModalBody className="modal-body">
          <Row className="g-3">
            <Col lg={12}>
              {manualData && manualData?.payment_status === "Success"
                ? renderManualPaymentJson()
                : ManualPaymentModalLayoutJSON?.map((element) => (
                    <div key={element.elementid}>
                      {element.child.map((child) =>
                        renderElement(child, fieldsJson, formState)
                      )}
                    </div>
                  ))}
              {/* {ManualPaymentModalLayoutJSON.map((element) => (
                <div key={element.elementid}>
                  {element.child.map((child) =>
                    renderElement(child, fieldsJson, formState)
                  )}
                </div>
              ))} */}
            </Col>
            {manualData?.payment_status !== "Success" && (
              <Col lg={12}>
                <div className="hstack gap-2 justify-content-end">
                  <Button
                    type="button"
                    className="primary-outline"
                    onClick={handleSaveDraft}
                    disabled={!isFormValid || isSubmitting}
                  >
                    Save draft
                  </Button>
                  <Button
                    type="submit"
                    className="primary primary_button"
                    value={"confirm"}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Confirming..." : "Confirm"}
                  </Button>
                </div>
              </Col>
            )}
          </Row>
        </ModalBody>
      </Form>
    </Modal>
  );
}

export default ManualPaymentModal;
